import { UnreachableCaseError } from "libs/errors";
import { DateTime, WeekdayNumbers } from "luxon";

export const SpecialDateEnum = {
  SOMEDAY: DateTime.fromObject({ year: 2500, month: 1, day: 1 }),
} as const;

type Month =
  | "January"
  | "February"
  | "March"
  | "April"
  | "May"
  | "June"
  | "July"
  | "August"
  | "September"
  | "October"
  | "November"
  | "December";

type Weekday = "Sunday" | "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday";

export function getDayAsNumber(day: "su" | "mo" | "tu" | "we" | "th" | "fr" | "sa" | Lowercase<Weekday> | Weekday) {
  // In luxon, weekday goes from 1 (Monday) to 7 (Sunday)
  // Convert to Luxon weekday format
  if (day.toLowerCase().startsWith("mo")) return Weekday.MONDAY;
  if (day.toLowerCase().startsWith("tu")) return Weekday.TUESDAY;
  if (day.toLowerCase().startsWith("we")) return Weekday.WEDNESDAY;
  if (day.toLowerCase().startsWith("th")) return Weekday.THURSDAY;
  if (day.toLowerCase().startsWith("fr")) return Weekday.FRIDAY;
  if (day.toLowerCase().startsWith("sa")) return Weekday.SATURDAY;
  if (day.toLowerCase().startsWith("su")) return Weekday.SUNDAY;
  throw new UnreachableCaseError(day as never, `[getDayAsNumber] invalid day: ${day}`);
}

export function getWeekEndsOn(weekStartsOn: "su" | "mo") {
  if (weekStartsOn === "su") return Weekday.SATURDAY;
  if (weekStartsOn === "mo") return Weekday.SUNDAY;
  throw new UnreachableCaseError(weekStartsOn as never, `[getWeekEndsOn] invalid weekStartsOn: ${weekStartsOn}`);
}

export const Weekday = {
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
  SUNDAY: 7,
} as const satisfies Record<string, WeekdayNumbers>;

export function getStartOfWeek(weekStartsOn: "su" | "mo", now: DateTime) {
  const daysSinceWeekStartDay = getDaysSinceWeekday(weekStartsOn, now);
  return now.minus({ days: daysSinceWeekStartDay }).startOf("day");
}

export function getStartOfWeekend(weekendStartsOn: "sa", now: DateTime) {
  // If the day is saturday then the start of the weekend is today.
  // If the day is sunday then the start of the weekend is yesterday.
  // Else the start of the weekend is the upcoming saturday.

  switch (now.weekday) {
    case Weekday.SATURDAY:
      return now.startOf("day");
    case Weekday.SUNDAY:
      return now.minus({ days: 1 }).startOf("day");
    default: {
      const daysToNextSaturday = Weekday.SATURDAY - now.weekday;
      return now.plus({ days: daysToNextSaturday }).startOf("day");
    }
  }
}

export function getDaysSinceWeekday(weekday: "su" | "mo" | "tu" | "we" | "th" | "fr" | "sa", now: DateTime) {
  // In this function we want to return the number of days since the user provided
  // "weekday" day.
  //
  // e.g. weekday = "su", currentDay = 1 (Monday)
  // 1 day
  //
  // e.g. weekday = "mo", currentDay = 1 (Monday)
  // 0 days

  const weekdayNumber = getDayAsNumber(weekday);
  const currentDay = now.weekday;

  if (currentDay >= weekdayNumber) {
    return currentDay - weekdayNumber;
  } else {
    // If current day is before the week start day in the week cycle,
    // it means we're counting from the previous week's start day
    return currentDay + 7 - weekdayNumber;
  }
}

export function getMonthAsNumber(month: Month | Lowercase<Month>) {
  switch (month.toLowerCase() as Lowercase<Month>) {
    case "january":
      return 1;
    case "february":
      return 2;
    case "march":
      return 3;
    case "april":
      return 4;
    case "may":
      return 5;
    case "june":
      return 6;
    case "july":
      return 7;
    case "august":
      return 8;
    case "september":
      return 9;
    case "october":
      return 10;
    case "november":
      return 11;
    case "december":
      return 12;
    default:
      throw new UnreachableCaseError(month as never, `[getMonthAsNumber] invalid month: ${month}`);
  }
}
